import React from 'react';
import { Link } from 'react-router-dom';
import { Panel } from 'primereact/panel';
import PropTypes from 'prop-types';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { useSelector } from 'react-redux';
import { Gr8piFormattedDate } from '../../components/Gr8piFormattedDateTime';

const formatOptions = locale => new Intl.NumberFormat(locale, { minimumFractionDigits: 2 });

const messages = defineMessages({
  header: { id: 'consumption.detail.header', defaultMessage: 'Consumption details' },
  respondentName: { id: 'consumption.table.respondent_name', defaultMessage: 'Respondent name' },
  respondentLanguage: { id: 'consumption.table.respondent_language', defaultMessage: 'Respondent language' },
  unindexedPrice: { id: 'consumption.table.unindexed_price', defaultMessage: 'Unindexed price' },
  indexedPrice: { id: 'consumption.table.indexed_price', defaultMessage: 'Indexed price paid' },
  respondentLocation: { id: 'consumption.respondent_location', defaultMessage: 'Respondent location' },
  userLocation: { id: 'consumption.user_location', defaultMessage: 'User location' },
  clientLocation: { id: 'consumption.client_location', defaultMessage: 'Client location' },
  dataNotAvailable: {
    id: 'consumption.account_consumption.data_table.body.dataNotAvailable',
    defaultMessage: 'Data not available',
  },
});

const ConsumptionDetail = ({ data, locale }) => {
  const { formatMessage } = useIntl();

  const {
    cldr: { localeDisplayName, teritoryDisplayName },
  } = useSelector(state => state.i18n);

  const { account } = data;
  const {
    id: accountId,
    companyInformation: { displayName: accountDisplayName },
  } = account;
  const { consumptionLog } = account;
  const {
    id: consumptionLogId,
    consumedOn,
    consumedBy,
    basedOn,
    type,
    country,
    priceIndex,
    items,
  } = consumptionLog;

  const consumedByTemplate = () => {
    if (consumedBy) {
      const { id: userId, firstName, lastName } = consumedBy;

      return (
        <Link to={`/accounts/${accountId}/users/${userId}`}>
          {`${firstName} ${lastName}`}
        </Link>
      );
    }

    return <span>{formatMessage(messages.dataNotAvailable)}</span>;
  };

  const basedOnTemplate = () => {
    if (basedOn === 'RESPONDENT_LOCATION') {
      return formatMessage(messages.respondentLocation);
    }

    if (basedOn === 'USER_LOCATION') {
      return formatMessage(messages.userLocation);
    }

    if (basedOn === 'ACCOUNT_LOCATION') {
      return formatMessage(messages.clientLocation);
    }

    return formatMessage(messages.dataNotAvailable);
  };

  const respondentNameTemplate = (columnData) => {
    if (columnData.item.details === '') {
      return formatMessage(messages.dataNotAvailable);
    }

    return columnData.item.details;
  };

  const respondentLanguageTemplate = (columnData) => {
    if (columnData.item.language === '') {
      return formatMessage(messages.dataNotAvailable);
    }

    return localeDisplayName(columnData.item.language);
  };

  const unindexedPriceTemplate = (columnData) => {
    const { price } = columnData;

    const formatter = formatOptions(locale);

    return formatter.format(price);
  };

  const indexedPriceTemplate = (columnData) => {
    const { indexedPrice } = columnData;

    const formatter = formatOptions(locale);

    return formatter.format(indexedPrice);
  };

  const respondentNameHeader = formatMessage(messages.respondentName);
  const respondentLanguageHeader = formatMessage(messages.respondentLanguage);
  const unindexedPriceHeader = formatMessage(messages.unindexedPrice);
  const indexedPriceHeader = formatMessage(messages.indexedPrice);
  const basedOnHeader = basedOnTemplate();
  const consumedByHeader = consumedByTemplate();

  return (
    <div className="p-grid">
      <div className="p-col-12">
        <div className="brand-detailed-wrapper">
          <Panel header={messages.header.defaultMessage}>
            <div className="p-grid">
              <div className="p-col-12">
                <p>
                  <FormattedMessage id="consumption.id" defaultMessage="ID" tagName="strong" />
                  <span>
                    {': '}
                    {consumptionLogId}
                  </span>
                </p>
                <p>
                  <FormattedMessage id="consumption.date" defaultMessage="Date" tagName="strong" />
                  <span>
                    {': '}
                    {consumedOn && <Gr8piFormattedDate dateValue={consumedOn} />}
                  </span>
                </p>
                <p>
                  <FormattedMessage id="consumption.type" defaultMessage="Type" tagName="strong" />
                  <span>
                    {': '}
                    {type}
                  </span>
                </p>
                <p>
                  <FormattedMessage id="consumption.country" defaultMessage="Consumed in" tagName="strong" />
                  <span>
                    {': '}
                    {teritoryDisplayName(country)}
                  </span>
                </p>
                <p>
                  <FormattedMessage id="consumption.locationIndex" defaultMessage="Location index" tagName="strong" />
                  <span>
                    {': '}
                    {priceIndex}
                  </span>
                </p>
                <p>
                  <FormattedMessage id="consumption.basedOn" defaultMessage="Based on" tagName="strong" />
                  <span>
                    {': '}
                    {basedOnHeader}
                  </span>
                </p>
                <p>
                  <FormattedMessage id="consumption.account" defaultMessage="Account" tagName="strong" />
                  <span>
                    {': '}
                    {accountDisplayName}
                  </span>
                </p>
                <p>
                  <FormattedMessage id="consumption.user" defaultMessage="User" tagName="strong" />
                  <span>
                    {': '}
                    {consumedByHeader}
                  </span>
                </p>
                <div className="p-col-12">
                  <DataTable value={items} responsive autoLayout>
                    <Column field="name" header={respondentNameHeader} body={respondentNameTemplate} />
                    <Column field="language" header={respondentLanguageHeader} body={respondentLanguageTemplate} />
                    <Column field="unindexedPrice" header={unindexedPriceHeader} body={unindexedPriceTemplate} />
                    <Column field="indexedPrice" header={indexedPriceHeader} body={indexedPriceTemplate} />
                  </DataTable>
                </div>
              </div>
            </div>
          </Panel>
        </div>
      </div>
    </div>
  );
};

ConsumptionDetail.propTypes = {
  data: PropTypes.objectOf(PropTypes.any).isRequired,
  locale: PropTypes.string.isRequired,
};

export default ConsumptionDetail;
